import is from '@sindresorhus/is';
import { useCallback, useMemo } from 'react';

import { useActiveThreadStore } from '../ActiveThreadStoreProvider';
import { useCommentsQuery } from '../api/use-comments-query';
import type { CommentHighlightState } from '../comment-highlight';
import { scrollAnchorIntoViewportIfNeeded } from '../scroll-anchor-into-view';
import { useRequestCommentsOpen } from '../surface/comments-open-request-context';
import type { CommentAnchor, CommentTarget } from '../types';
import { descriptionAnchorAttrsFor, skillAnchorAttrs, titleAnchorAttrs, workflowStepPartAnchorAttrs } from './grammar';
import { resolveAnchor } from './resolve';
import { useOpenGroupThread } from './use-open-group-thread';

export type CommentableAnchorTarget =
    | {
          readonly kind: 'title';
          readonly stepIndex?: undefined;
          readonly itemId: string;
          readonly skillId?: undefined;
          readonly sectionId?: undefined;
          readonly workflowStepId?: undefined;
      }
    | {
          readonly kind: 'skill';
          readonly stepIndex?: undefined;
          readonly itemId: string;
          readonly skillId: string;
          readonly sectionId?: undefined;
          readonly workflowStepId?: undefined;
      }
    | {
          readonly kind: 'description-section';
          readonly stepIndex?: undefined;
          readonly itemId: string;
          readonly sectionId: string;
          readonly skillId?: undefined;
          readonly workflowStepId?: undefined;
      }
    // Work-task vocabulary: one description, and workflow steps addressed by position.
    | {
          readonly kind: 'description';
          readonly itemId: string;
          readonly sectionId?: undefined;
          readonly skillId?: undefined;
          readonly workflowStepId?: undefined;
      }
    | {
          readonly kind: 'workflow-step-title';
          readonly itemId: string;
          readonly stepIndex: number;
          readonly sectionId?: undefined;
          readonly skillId?: undefined;
          readonly workflowStepId?: undefined;
      }
    | {
          readonly kind: 'workflow-step-description';
          readonly itemId: string;
          readonly stepIndex: number;
          readonly sectionId?: undefined;
          readonly skillId?: undefined;
          readonly workflowStepId?: undefined;
      };

export type StartCommentOptions = {
    readonly anchorRect?: DOMRect;
    readonly anchor?: CommentAnchor | null;
    readonly commentId?: string;
};

export type CommentableAnchor = {
    readonly attrs: Record<string, string>;
    readonly state: CommentHighlightState;
    readonly commentId: string | undefined;
    readonly openThread: (commentId?: string) => void;
    readonly startComment: (anchorEl: HTMLElement | null, options?: StartCommentOptions) => void;
};

const matchesTarget = (target: CommentTarget, params: CommentableAnchorTarget): boolean => {
    if (params.kind === 'title') {
        return target.kind === 'title';
    }
    if (params.kind === 'skill') {
        return target.kind === 'skill' && target.skillId === params.skillId;
    }
    if (params.kind === 'workflow-step-title' || params.kind === 'workflow-step-description') {
        return target.kind === params.kind && target.stepIndex === params.stepIndex;
    }
    if (params.kind === 'description') {
        return target.kind === 'description';
    }
    return target.kind === 'description-section' && target.sectionId === params.sectionId;
};

export const useOpenThread = (itemId: string): ((threadId: string, target: CommentTarget) => void) => {
    const setActiveThreadId = useActiveThreadStore((state) => state.setActiveThreadId);
    const setIsCommentsOpen = useActiveThreadStore((state) => state.setIsCommentsOpen);
    const openGroupThread = useOpenGroupThread();
    return useCallback(
        (threadId, target) => {
            setIsCommentsOpen(true);
            setActiveThreadId(threadId);
            openGroupThread(threadId, target);
            const anchor = resolveAnchor({ itemId, commentId: threadId, target });
            if (is.null(anchor)) return;
            scrollAnchorIntoViewportIfNeeded(anchor, anchor.getBoundingClientRect());
        },
        [itemId, openGroupThread, setActiveThreadId, setIsCommentsOpen],
    );
};

export const useCommentableAnchor = (target: CommentableAnchorTarget): CommentableAnchor => {
    const stepIndex =
        target.kind === 'workflow-step-title' || target.kind === 'workflow-step-description'
            ? target.stepIndex
            : undefined;
    const { data: comments } = useCommentsQuery();
    const activeThreadId = useActiveThreadStore((state) => state.activeThreadId);
    const pendingTarget = useActiveThreadStore((state) => state.pendingTarget);
    const setPendingTarget = useActiveThreadStore((state) => state.setPendingTarget);
    const openThreadAction = useOpenThread(target.itemId);
    const requestCommentsOpen = useRequestCommentsOpen();

    const savedComment = comments?.find(
        (comment) => is.nullOrUndefined(comment.resolution) && matchesTarget(comment.target, target),
    );
    const isActive = !is.undefined(savedComment) && activeThreadId === savedComment.id;
    const hasPending = !is.null(pendingTarget) && matchesTarget(pendingTarget.target, target);

    const resolveState = (): CommentHighlightState => {
        if (isActive) return 'active';
        if (!is.undefined(savedComment)) return 'saved';
        if (hasPending) return 'pending';
        return 'none';
    };

    const commentId = savedComment?.id;

    const commentTarget = useMemo<CommentTarget>(() => {
        if (target.kind === 'title') {
            return { kind: 'title' };
        }
        if (target.kind === 'skill') {
            return { kind: 'skill', skillId: target.skillId };
        }
        if (target.kind === 'workflow-step-title' || target.kind === 'workflow-step-description') {
            return { kind: target.kind, stepIndex: target.stepIndex };
        }
        if (target.kind === 'description') {
            return { kind: 'description' };
        }
        return { kind: 'description-section', sectionId: target.sectionId };
    }, [target.kind, target.skillId, target.sectionId, stepIndex]);

    const openThread = useCallback(
        (explicitThreadId?: string): void => {
            const threadId = is.string(explicitThreadId) ? explicitThreadId : savedComment?.id;
            if (is.undefined(threadId)) return;
            openThreadAction(threadId, commentTarget);
        },
        [savedComment, openThreadAction, commentTarget],
    );

    const startComment = useCallback(
        (anchorEl: HTMLElement | null, options?: StartCommentOptions): void => {
            if (target.kind !== 'description-section' && target.kind !== 'description' && !is.undefined(savedComment)) {
                openThread();
                return;
            }
            requestCommentsOpen();
            setPendingTarget({
                target: commentTarget,
                anchorRect: options?.anchorRect ?? anchorEl?.getBoundingClientRect() ?? new DOMRect(),
                anchorEl,
                anchor: options?.anchor,
                commentId: options?.commentId ?? crypto.randomUUID(),
            });
        },
        [target.kind, savedComment, openThread, requestCommentsOpen, setPendingTarget, commentTarget],
    );

    const attrs = useMemo(() => {
        if (target.kind === 'title') {
            return titleAnchorAttrs({ itemId: target.itemId, commentId });
        }
        if (target.kind === 'skill') {
            return skillAnchorAttrs({ itemId: target.itemId, skillId: target.skillId, commentId });
        }
        if (target.kind === 'workflow-step-title' || target.kind === 'workflow-step-description') {
            return workflowStepPartAnchorAttrs({
                commentId,
                itemId: target.itemId,
                kind: target.kind,
                stepIndex: target.stepIndex,
            });
        }
        return descriptionAnchorAttrsFor(
            target.kind === 'description' ? 'description' : 'description-section',
            target.itemId,
        );
    }, [target.kind, target.itemId, target.skillId, target.workflowStepId, stepIndex, commentId]);

    return { attrs, state: resolveState(), commentId, openThread, startComment };
};
